import type { ReactNode } from 'react';
import { Label, cn } from '@immo/ui-kit';

export type ComposerFieldRowProps = {
  readonly label: string;
  // Associates the label with the field's input, so clicking the label focuses it.
  readonly htmlFor?: string;
  // Horizontal padding (`px-6` / `px-3`) matching the composer container; the row
  // itself is full-bleed (its parent supplies the negative margin), so the divider
  // spans the whole width while this keeps the content aligned.
  readonly padX: string;
  // Optional control at the row's end — e.g. the To row's Cc/Bcc toggle chip.
  readonly trailing?: ReactNode;
  // Validation message shown beneath the field, above the divider.
  readonly error?: string | undefined;
  readonly children: ReactNode;
};

/**
 * One composer header row (design): an inline label, a borderless field that fills
 * the row, and a full-width rule beneath — the rows' only boundaries. From / To /
 * Cc / Bcc / Subject all use this, so the header reads as a single ruled stack with
 * no per-field boxes. The field itself (passed as `children`) is borderless and
 * transparent; this row owns the divider, the label column, and the padding.
 */
export function ComposerFieldRow({ label, htmlFor, padX, trailing, error, children }: ComposerFieldRowProps) {
  return (
    <div className={cn('border-b border-border py-2', padX)}>
      <div className="flex items-center gap-2">
        <Label htmlFor={htmlFor} className="w-14 shrink-0 text-xs">
          {label}
        </Label>
        <div className="min-w-0 flex-1">{children}</div>
        {trailing}
      </div>
      {error !== undefined && (
        <p role="alert" className="mt-1 text-xs text-danger-600">
          {error}
        </p>
      )}
    </div>
  );
}
